import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { BreadcrumbGroupProps } from "@cloudscape-design/components";
import { ROUTES } from "src/constants";
import { WORKFLOW_TAGS } from "src/constants/workflow-tags";

interface BreadcrumbConfig {
  text: string;
  href: string;
}

interface RouteConfig {
  path: string;
  breadcrumb: string;
  parent?: string;
}

// Route configuration for breadcrumbs
const routeConfigs: RouteConfig[] = [
  {
    path: ROUTES.DASHBOARD,
    breadcrumb: "Dashboard",
  },
  {
    path: ROUTES.REPORTS_ALL,
    breadcrumb: "Reports & Analysis",
    parent: ROUTES.DASHBOARD,
  },
  {
    path: ROUTES.ACTIVITIES_ALL,
    breadcrumb: "All Activities",
    parent: ROUTES.DASHBOARD,
  },
  {
    path: ROUTES.MFR_VIEW,
    breadcrumb: WORKFLOW_TAGS.MFR.label,
    parent: ROUTES.REPORTS_ALL,
  },
  {
    path: ROUTES.MFR_CREATE,
    breadcrumb: "Create",
    parent: ROUTES.MFR_VIEW,
  },
  {
    path: ROUTES.MFR_EDIT,
    breadcrumb: "Edit",
    parent: ROUTES.MFR_VIEW,
  },
  {
    path: ROUTES.MFR_RUN,
    breadcrumb: "Run",
    parent: ROUTES.MFR_VIEW,
  },
  {
    path: ROUTES.MFR_REPORT,
    breadcrumb: "Report",
    parent: ROUTES.MFR_VIEW,
  },
  {
    path: ROUTES.NETPPM_VIEW,
    breadcrumb: WORKFLOW_TAGS.PPM.label,
    parent: ROUTES.REPORTS_ALL,
  },
  {
    path: ROUTES.NETPPM_CREATE,
    breadcrumb: "Create",
    parent: ROUTES.NETPPM_VIEW
  },
  {
    path: ROUTES.NETPPM_RUN,
    breadcrumb: "Run",
    parent: ROUTES.NETPPM_VIEW,
  },
  {
    path: ROUTES.NETPPM_REPORT,
    breadcrumb: "Report",
    parent: ROUTES.NETPPM_VIEW,
  },
  {
    path: ROUTES.RVM_VIEW,
    breadcrumb: "RVM",
    parent: ROUTES.REPORTS_ALL,
  },
  {
    path: ROUTES.RVM_CREATE,
    breadcrumb: "Create",
    parent: ROUTES.RVM_VIEW,
  },
  {
    path: ROUTES.RVM_RUN,
    breadcrumb: "Run",
    parent: ROUTES.RVM_VIEW,
  },
  {
    path: ROUTES.RVM_REPORT,
    breadcrumb: "Report",
    parent: ROUTES.RVM_VIEW,
  },
  {
    path: ROUTES.SKILL_MANAGEMENT,
    breadcrumb: "Skill Management",
    parent: ROUTES.DASHBOARD,
  },
  {
    path: ROUTES.SKILL_VIEW,
    breadcrumb: "Skill",
    parent: ROUTES.SKILL_MANAGEMENT,
  },
  // Future routes can be added here:
  // {
  //   path: '/reports-and-analysis/:id',
  //   breadcrumb: 'Report Details', // This could be dynamic based on report name
  //   parent: '/reports-and-analysis/all',
  // },
];

export const useBreadcrumbs = (): BreadcrumbGroupProps.Item[] => {
  const location = useLocation();

  const breadcrumbs = useMemo(() => {
    const currentPath = location.pathname;

    // Find the current route config
    const currentRoute = routeConfigs.find((route) => {
      // Handle exact matches first
      if (route.path === currentPath) {
        return true;
      }

      // Handle dynamic routes (for future use)
      if (route.path.includes(":")) {
        // First escape special regex characters, then replace route parameters
        let escapedPath = route.path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const routePattern = escapedPath.replace(/:[^/]+/g, "[^/]+");
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(currentPath);
      }

      return false;
    });

    if (!currentRoute) {
      // Default to Dashboard if no route found
      return [{ text: "Dashboard", href: "/dashboard" }];
    }

    // Build breadcrumb chain
    const breadcrumbChain: BreadcrumbConfig[] = [];
    let currentConfig: RouteConfig | undefined = currentRoute;

    // Build the chain by following parent relationships
    // Track visited paths to prevent infinite loops from circular references
    const visitedPaths = new Set<string>();
    while (currentConfig && !visitedPaths.has(currentConfig.path)) {
      visitedPaths.add(currentConfig.path);

      breadcrumbChain.unshift({
        text: currentConfig.breadcrumb,
        href: currentConfig.path,
      });

      if (currentConfig.parent) {
        currentConfig = routeConfigs.find(
          (route) => route.path === currentConfig!.parent
        );
      } else {
        currentConfig = undefined;
      }
    }

    // Convert to Cloudscape breadcrumb format
    return breadcrumbChain.map((crumb, index) => ({
      text: crumb.text,
      href: crumb.href,
    }));
  }, [location.pathname]);

  return breadcrumbs;
};

// Helper function to add new route configurations dynamically
export const addRouteConfig = (config: RouteConfig) => {
  const existingIndex = routeConfigs.findIndex(
    (route) => route.path === config.path
  );
  if (existingIndex >= 0) {
    routeConfigs[existingIndex] = config;
  } else {
    routeConfigs.push(config);
  }
};

// Helper function to get route config for a specific path
export const getRouteConfig = (path: string): RouteConfig | undefined => {
  return routeConfigs.find((route) => route.path === path);
};
